import React, {Fragment} from 'react'
import ReactDOM from 'react-dom'
import {connect} from 'react-redux'
import {Loadable} from 'common/Loadable'
import {MapV2} from 'common/MapV2'
import {TrackSnapPoint} from 'common/MapV2/TrackSnapPoint'
import {FullscreenButton} from 'common/MapV2/controls/FullscreenButton'
import {SettingsMenu} from 'common/MapV2/controls/SettingsMenu'
import {Topo} from 'common/styleguide/Topo'
import {PhotoClusters} from 'common/Maplet/PhotoClusters'
import {POIv3} from 'common/Map/POI'
import {segmentPopoverClearance} from 'common/Map/mapUtils'
import {TrackLine} from 'common/MapV2/TrackLine'
import {DroppablePage} from 'common/DroppablePage'
import {MetricUnitsContext, withMetricUnits} from 'common/units/MetricUnitsContext'
import {tripActions, tripSelectors} from 'data/resources/trips'
import {routeActions, routeSelectors} from 'data/resources/routes'
import {photoActions, photoSelectors} from 'data/resources/photos'
import {pointOfInterestSelectors} from 'data/resources/pointsOfInterest'
import {showGallery} from 'features/gallery/showGallery'
import {apiv3} from 'lib/apiv3'
import {softError} from 'lib/softError'
import fetchJSON from 'lib/fetchJSON'
import {getPhotoUploadMaxConcurrency, currentURLWithQueryParams, queryStringSet} from 'lib/utils'
import * as userActions from 'lib/api/currentUser'
import {showModal} from 'lib/modalUtils'
import {SHARE_MODAL} from 'common/modals/ShareModal'
import {TROUTE_INFO_MODAL} from 'common/modals/TRouteInfoModal'
import {FLAG_USER_AS_IMPORTANT_DIALOG, TROUTE_DELETE_PHOTOS_MODAL} from 'common/dialogs/AdminActionDialogs'
import {ExportSvgMenuItem} from 'common/GlobalNav/AdminMenu/Items'
import {urls} from 'lib/urls'
// import styles from './TRouteShowPage.scss'
import {ThreePanelLayout, MainPanel, BottomPanel, NoticePanel} from 'common/layouts/ThreePanelLayout'
// use a special type of SidePanel instead of the basic one
import {TRouteSidePanel} from './TRouteSidePanel'
import {TRouteBottomPanel} from './TRouteBottomPanel'
import {AdminMenuItem} from 'common/GlobalNav/AdminMenu'
import PromisePool from 'es6-promise-pool'
// we import and bind these because they're long and complex enough to deserve
// their own file, imports, etc, but it's very convenient to access this.props
// directly instead of booting up a component
import {renderSelectedFeature, renderHoveredFeature} from './mapRendering'
import {featureTypes} from 'common/MapV2/featureTypes'
import _ from 'lodash'
import {OrderedMap} from 'immutable'
import {TrackLocation, Viewport} from 'data/records'
import {tileServices} from 'lib/tileServices'
const MetricAwareTRouteBottomPanel = withMetricUnits(TRouteBottomPanel)

const TRSP_NOTICE_DISMISSED = 'trsp_notice_dismissed'
const TRSP_LAUNCH = '2020-02-04'

const trouteActions = objectType => (
  objectType === 'trip'
    ? tripActions
    : routeActions
)

const trouteSelectors = objectType => (
  objectType === 'trip'
    ? tripSelectors
    : routeSelectors
)

const unitsOptions = OrderedMap({
  imperial: 'Imperial',
  metric: 'Metric'
})

const userPrefMappings = {
  showDistanceMarkers: 'route_viewer_enable_distance_markers',
  metricUnits: 'metric_units',
  scrollWheelZoom: 'route_viewer_enable_scrollwheel_zoom',
  showPOIs: 'route_viewer_show_pois'
}

const parseQuerySegment = (location) => {
  if (!location || !location.query || !location.query.highlight) {
    return null
  }
  const arr = location.query.highlight.split('-').map(n => parseInt(n, 10))
  if (arr.length === 2 && arr.every(n => isFinite(n))) {
    return arr
  }
  return null
}

// Here's the rub now: we want the RSP and TSP to share the same codebase.
// The structure here is *nearly* the same, so here's the plan:
// We'll share the layout code, focus management, etc.
// We will need to have conditionals for
// - proper sharing URLs
// - inline editing
// - modal editing
// - S2D
class _TRouteShowPage extends React.PureComponent {
  constructor (props) {
    super(props)

    const metricUnits = props.currentUser.id
      ? props.currentUser.preferences.get('metric_units')
      : props.troute.metricUnits

    const showDistanceMarkers = props.currentUser.preferences.get('route_viewer_enable_distance_markers')
    const scrollWheelZoom = props.currentUser.preferences.get('route_viewer_enable_scrollwheel_zoom', true)
    const showPOIs = props.currentUser.preferences.get('route_viewer_show_pois')
    const highlightSlice = parseQuerySegment(props.location)

    const selectedFeature = highlightSlice && {
      type: featureTypes.SAMPLE_GRAPH_USER_SELECTION,
      data: {
        highlightSlice: [
          TrackLocation({left: highlightSlice[0], percent: 0}),
          TrackLocation({left: highlightSlice[1], percent: 0})
        ]
      }
    }

    const initialViewport = selectedFeature
      ? Viewport.containingSlice(selectedFeature.data.highlightSlice, props.troute.trackPoints)
      : Viewport({bounds: props.troute && props.troute.boundingBox})

    this.state = {
      fullscreen: false,
      selectedFeature,
      hoveredFeature: null,
      initialTRoute: props.troute,
      hasUnsavedChanges: false,
      showDebugWindow: !!window.location.search.includes('DEBUG'),
      noticeDismissed: false,
      overlay: tileServices.rwgps,
      initialViewport,
      // since we default to rwgps overlay on construction, padding only gets
      // passed into the MapboxMapEngine (this is to deal with highlight slices
      // in the url). if we ever allow for a different overlay on construction,
      // this value will need to be passed into/handled by GoogleMapEngine
      initialViewportPadding: selectedFeature && segmentPopoverClearance,
      zoom: initialViewport.zoom,
      mapSettings: {
        metricUnits,
        showDistanceMarkers,
        scrollWheelZoom,
        showPOIs
      }
    }
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (prevState.selectedFeature !== this.state.selectedFeature) {
      this.handleSelectedFeatureChange(prevState.selectedFeature, this.state.selectedFeature)
    }
  }

  componentWillUnmount () {
    if (this.state.hasUnsavedChanges) {
      this.revertDirtyTRoute()
    }
  }

  // REMEMBER that either of these features could be null!
  handleSelectedFeatureChange = (prevFeature, currentFeature) => {
    // less typing = more better
    const g = _.get
    // ignore switching from a feature that had a highlightSlice to a
    // SAMPLE_GRAPH_USER_SELECTION, it's just the user twiddling the sample graph
    if (g(prevFeature, 'data.highlightSlice') && g(currentFeature, 'type') === featureTypes.SAMPLE_GRAPH_USER_SELECTION) {
      return
    }

    // if we're going from a no-highlight-slice to yes-highlight-slice, zoom!
    if (!g(prevFeature, 'data.highlightSlice') && g(currentFeature, 'data.highlightSlice')) {
      const {troute} = this.props
      const {highlightSlice} = currentFeature.data

      queryStringSet(this.props.location, 'highlight', highlightSlice.map(s => s.left).join('-'))
      const vp = Viewport.containingSlice(highlightSlice, troute.trackPoints)
      this._map._mapEngine.flyTo(vp, {padding: segmentPopoverClearance})
      return
    }

    // if we're going from a yes-highlight-slice to no feature selected, reset zoom
    if (!currentFeature && g(prevFeature, 'data.highlightSlice')) {
      const {troute} = this.props

      queryStringSet(this.props.location, 'highlight', undefined) // clear slice from url
      const vp = Viewport({bounds: troute.boundingBox})
      this._map._mapEngine.flyTo(vp)
      return // eslint-disable-line
    }
  }

  toggleDebugWindow = () => {
    this.setState({showDebugWindow: !this.state.showDebugWindow})
  }

  onFeatureSelected = (selectedFeature) => {
    // hoveredFeature always gets cleared when the selected feature is
    // changed or updated
    this.setState({selectedFeature, hoveredFeature: null})
  }

  onFeatureHovered = (hoveredFeature) => {
    this.setState({hoveredFeature})
  }

  clearSelectedFeature = () => {
    this.setState({selectedFeature: null})
  }

  clearHoveredFeature = () => {
    this.setState({hoveredFeature: null})
  }

  renderSelectedFeature = renderSelectedFeature.bind(this)

  renderHoveredFeature = renderHoveredFeature.bind(this)

  onMapSnap = ({snapPoint, snapIndex}) => {
    this.onFeatureHovered({
      type: featureTypes.POINT_GENERIC,
      data: {highlightPoint: {trackPoint: snapPoint, index: snapIndex}}
    })
  }

  renderDebugWindow = () => {
    const style = {
      position: 'fixed',
      padding: 20,
      right: 20,
      top: 130,
      zIndex: 9999,
      maxHeight: window.innerHeight - 360 + 'px',
      maxWidth: '350px',
      overflow: 'auto',
      background: '#FFF',
      fontSize: '13px'
    }

    // feel free to add/remove things from this as needed when you
    // are debugging features, just remember to reset it to just
    // selectedFeature and hoveredFeature when you go to make a PR.
    //
    // PROTIP: you can make the map inspector persist through reloads
    // by adding &DEBUG to the URL params
    const itemsToInspect = [
      ['selectedFeature', this.state.selectedFeature],
      ['hoveredFeature', this.state.hoveredFeature]
    ]

    return ReactDOM.createPortal(<div style={style}>
      {itemsToInspect.map(([label, data]) => <Fragment key={label}>
        <strong>{label}</strong>
        <pre>{JSON.stringify(data, null, '  ')}</pre>
      </Fragment>)}
    </div>, document.body)
  }

  noticePanelDismissClicked = () => {
    this.props.setPreference(TRSP_NOTICE_DISMISSED, true)
    this.setState({noticeDismissed: true}, () => this._map._mapEngine.resizeToParentHeight())
  }

  onUnitsChange = (selectedUnits) => {
    const {currentUser, setPreference} = this.props
    const metricUnits = selectedUnits === 'metric'
    if (currentUser.id) {
      setPreference(userPrefMappings['metricUnits'], metricUnits)
    }
    this.setState({mapSettings: {...this.state.mapSettings, metricUnits}})
  }

  onDistanceMarkersChange = (showDistanceMarkers) => {
    const {currentUser, setPreference} = this.props
    if (currentUser.id) {
      setPreference(userPrefMappings['showDistanceMarkers'], showDistanceMarkers)
    }
    this.setState({mapSettings: {...this.state.mapSettings, showDistanceMarkers}})
  }

  onPOIsChange = showPOIs => {
    const {currentUser, setPreference} = this.props
    if (currentUser.id) {
      setPreference(userPrefMappings['showPOIs'], showPOIs)
    }
    this.setState({mapSettings: {...this.state.mapSettings, showPOIs}})
  }

  onScrollWheelZoomChange = (scrollWheelZoom) => {
    const {currentUser, setPreference} = this.props
    if (currentUser.id) {
      setPreference(userPrefMappings['scrollWheelZoom'], scrollWheelZoom)
    }
    this.setState({mapSettings: {...this.state.mapSettings, scrollWheelZoom}})
  }

  toggleFullscreen = () => {
    this.setState(
      {fullscreen: !this.state.fullscreen},
      () => this._map._mapEngine.resizeToParentHeight()
    )
  }

  revertUnsavedChanges = () => this.setState({hasUnsavedChanges: false})

  dirtyUpdateTRoute = (record) => {
    this.props.dirtyUpdateTRoute(record)
    this.setState({hasUnsavedChanges: true})
  }

  revertDirtyTRoute = () => {
    const {dirtyUpdateTRoute} = this.props
    const {initialTRoute} = this.state
    dirtyUpdateTRoute(initialTRoute)
    this.setState({hasUnsavedChanges: false})
  }

  onViewportChanged = (newViewport) => {
    this.setState({zoom: newViewport.zoom})
  }

  render () {
    const {
      currentUser,
      objectType,
      location,
      onTriggerFileBrowser,
      pendingPhotoUploads,
      troute,
      troutePhotos,
      troutePOIs,
      removePhoto,
      updateTRoute
    } = this.props

    const {
      selectedFeature,
      hoveredFeature,
      showDebugWindow,
      noticeDismissed,
      overlay,
      fullscreen,
      initialViewport,
      initialViewportPadding,
      zoom,
      mapSettings
    } = this.state

    if (!troute) {
      // This situation can occur momentarily on route deletion, after the route
      // has been deleted but before browserHistory.replace('/') call takes effect.
      // We need to handle it nicely or the error will trigger our errorbound UI
      return null
    }

    // if we want to nudge folks in the future, should do a date comparison
    // here instead of just checking for the presence of the pref
    const showNotice = objectType === 'trip' &&
      !currentUser.preferences.get(TRSP_NOTICE_DISMISSED) &&
      !noticeDismissed &&
      currentUser.createdAt < TRSP_LAUNCH

    return (
      <MetricUnitsContext.Provider value={mapSettings.metricUnits}>
        <ThreePanelLayout fullscreen={fullscreen}>
          {showNotice && <NoticePanel onRequestClose={this.noticePanelDismissClicked}>
            You are currently viewing the new ride page. <a href={currentURLWithQueryParams({beta: false})}>Return to classic ride page</a>
          </NoticePanel>}
          <TRouteSidePanel
            currentUser={currentUser}
            objectType={objectType}
            onTriggerFileBrowser={onTriggerFileBrowser}
            pendingPhotoUploads={pendingPhotoUploads}
            troute={troute}
            troutePhotos={troutePhotos}
            onPhotoDeleted={removePhoto}
            selectedFeature={selectedFeature}
            onFeatureHovered={this.onFeatureHovered}
            onFeatureSelected={this.onFeatureSelected} />
          <MainPanel>
            <Topo style={{height: '100%', width: '100%'}}>
              {/* This is a little odd-looking, but the AdminMenuItem component
                needs to go inside one of the *Panel components or else it won't
                mount */}
              <AdminMenuItem
                menuKey='toggle_trsp_debug'
                label='Toggle map state inspector'
                onClick={this.toggleDebugWindow} />
              <AdminMenuItem
                menuKey='api_history'
                label='API History'
                href={`/users/${troute.userId}/api_history`} />
              <AdminMenuItem
                menuKey='clear_distances'
                label='Clear Distances'
                onClick={() => fetchJSON(`/${objectType}s/${troute.id}/clear_distances`, null, {method: 'POST'}).then(_ => window.location.reload())} />
              <AdminMenuItem
                menuKey='clear_speeds'
                label='Clear Speeds'
                onClick={() => fetchJSON(`/${objectType}s/${troute.id}/clear_speeds`, null, {method: 'POST'}).then(_ => window.location.reload())} />
              <AdminMenuItem
                menuKey='delete_photos'
                label='Delete Photos'
                onClick={() => showModal(TROUTE_DELETE_PHOTOS_MODAL, {objectType, troute})} />
              <ExportSvgMenuItem />
              <AdminMenuItem
                menuKey='flag_as_important'
                label='Flag as important'
                onClick={() => showModal(FLAG_USER_AS_IMPORTANT_DIALOG, {userId: troute.userId})} />
              {objectType === 'route' && <AdminMenuItem
                label='Show route info'
                onClick={() => showModal(TROUTE_INFO_MODAL, {troute, objectType, objectId: troute.id})} />}
              {showDebugWindow && this.renderDebugWindow()}
              <MapV2
                scrollWheelZoom={mapSettings.scrollWheelZoom}
                overlay={overlay}
                onChangeOverlay={overlay => this.setState({overlay})}
                ref={r => { this._map = r }}
                onViewportChanged={this.onViewportChanged}
                initialViewport={initialViewport}
                initialViewportPadding={initialViewportPadding}
                controls={<FullscreenButton isFullscreen={fullscreen} onClick={this.toggleFullscreen} />}>
                <SettingsMenu
                  mapSettings={mapSettings}
                  onUnitsChange={this.onUnitsChange}
                  onDistanceMarkersChange={this.onDistanceMarkersChange}
                  onPOIsChange={this.onPOIsChange}
                  onScrollWheelZoomChange={this.onScrollWheelZoomChange}
                  unitsOptions={unitsOptions} />
                <TrackLine trackPoints={troute.trackPoints} showDistanceMarkers={mapSettings.showDistanceMarkers} />
                <TrackSnapPoint hoveredFeature={hoveredFeature} troute={troute} onSnap={this.onMapSnap} onUnSnap={this.clearHoveredFeature} />
                {/* TODO refactor troutePhotos to accept photos as a separate prop */}
                {troutePhotos && <PhotoClusters troute={troute.set('photos', troutePhotos)} clusterStrategy={objectType} zoom={zoom} />}
                {mapSettings.showPOIs && troutePOIs && troutePOIs.map(poi => <POIv3 key={poi.id} poi={poi} photos={troutePhotos} />)}
                {this.renderSelectedFeature()}
                {this.renderHoveredFeature()}
              </MapV2>
            </Topo>
          </MainPanel>
          <BottomPanel>
            <MetricAwareTRouteBottomPanel
              troute={troute}
              updateDirtyTRoute={this.dirtyUpdateTRoute}
              revertDirtyTRoute={this.revertDirtyTRoute}
              key={'' + fullscreen}
              objectType={objectType}
              location={location}
              currentUser={currentUser}
              updateTRoute={updateTRoute}
              revertUnsavedChanges={this.revertUnsavedChanges}
              selectedFeature={selectedFeature}
              hoveredFeature={hoveredFeature}
              onFeatureSelected={this.onFeatureSelected}
              onFeatureHovered={this.onFeatureHovered} />
          </BottomPanel>
        </ThreePanelLayout>
      </MetricUnitsContext.Provider>
    )
  }
}

class DroppableTRouteShowPage extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      pendingUploads: 0
    }
  }

  componentDidMount () {
    // recording the recentActivity here because the TRouteShowPage component
    // gets double mounted based on userIsOwner below
    const {troute, trouteId, objectType, currentUser} = this.props
    if (troute && troute.isStationary) {
      window.location.href = currentURLWithQueryParams({beta: 'false'})
      return
    }
    const capitalizedObjectType = `${objectType[0].toUpperCase()}${objectType.slice(1)}`
    trouteId && currentUser.id && apiv3.users.recordActivity('viewed', capitalizedObjectType, trouteId)
    this.showShareDialogIfShareInURL()
    this.showGalleryIfPhotoInURL()
  }

  showShareDialogIfShareInURL = () => {
    const {troute, objectType} = this.props
    if (troute && window.location.search.match(/\?openShare=true/)) {
      showModal(SHARE_MODAL, {
        objectType,
        objectId: troute.id,
        objectTitle: troute.name,
        objectUrl: urls[`${objectType}s`].show(troute.id, troute.privacyCode),
        privacyCode: troute.privacyCode
      })
    }
  }

  // copied from rsp
  // This isn't exactly elegant, but it's very functional and allows us to
  // easily support the old fragment-based url scheme for photos without
  // upgrading react-router
  showGalleryIfPhotoInURL () {
    const galleryIsOpen = document.getElementById('#react-gallery') // again, I never said it was elegant
    if (this._didShowGallery || galleryIsOpen) { return } // use a flag to prevent weird state clobbering

    const {troute, objectType, troutePhotos, currentUser, removePhoto} = this.props
    if (troute && window.location.hash.match(/#photos\/\d+$/)) {
      this._didShowGallery = true
      const userIsOwner = troute.userId === currentUser.id
      const activePhotoId = parseInt(window.location.hash.match(/#photos\/(\d+)$/)[1])
      const orderedPhotos = _.orderBy(troutePhotos, 'id', 'desc')
      showGallery({
        photos: orderedPhotos,
        activePhotoId,
        readonly: !userIsOwner,
        parentType: objectType,
        parentId: troute.id,
        onPhotoDeleted: removePhoto
      })
    }
  }

  onDrop = (files) => {
    const {addPhoto} = this.props
    this.setState((prevState) => ({pendingUploads: prevState.pendingUploads + files.length}))
    let index = 0
    const nextUpload = () => {
      if (!files[index]) { return null }
      const nextUpload = files[index]
      index++

      return addPhoto(nextUpload)
        .then(() => this.setState((prevState) => ({pendingUploads: prevState.pendingUploads - 1})))
    }
    const pool = new PromisePool(nextUpload, getPhotoUploadMaxConcurrency())

    pool.start()
  }

  onTriggerFileBrowser = () => {
    this._dropzone && this._dropzone.open()
  }

  render () {
    const {currentUser, troute} = this.props
    const {pendingUploads} = this.state
    const userIsOwner = troute && (currentUser.id === troute.userId)
    return userIsOwner
      ? <DroppablePage
        ref={r => { this._dropzone = r }}
        multiple
        onDrop={this.onDrop}>
        <_TRouteShowPage {...this.props} onTriggerFileBrowser={this.onTriggerFileBrowser} pendingPhotoUploads={pendingUploads} />
      </DroppablePage>
      : <_TRouteShowPage {...this.props} />
  }
}

/*
DEV NOTE - we want to use redux with loadable.

The historical problem here is that the Loadable needs to be able to dispatch,
but the TRouteShowPage wants to have a mapStateToProps.

The cleanest solution is to put the connect at the top level:

connect() -> Loadable -> Component

And just make sure to proxy the props into the <Loadable> invocation, which
will transparently pass through props down to the component it renders.
*/

class LoadableTRouteShowPage extends React.PureComponent {
  render () {
    const {troute, loadTRoute} = this.props
    return <Loadable
      resourceLoaded={!!troute || loadTRoute}
      component={DroppableTRouteShowPage}
      {...this.props} />
  }
}

const mapStateToProps = (state, ownProps) => {
  const troute = trouteSelectors(ownProps.objectType).find(state, ownProps.trouteId)
  return {
    currentUser: state.currentUser,
    troute,
    troutePhotos: troute && photoSelectors.findByIds(state, troute.photoIds),
    troutePOIs: troute && pointOfInterestSelectors.findByIds(state, troute.poiIds)
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const {objectType, trouteId, privacyCode} = ownProps

  const objectActions = trouteActions(objectType)

  return {
    addPhoto: (photo) => {
      const body = new window.FormData()
      body.append('file', photo)
      body.append('parent_type', objectType)
      body.append('parent_id', trouteId)

      return dispatch(photoActions.create(body))
        .then(r => dispatch(objectActions.photoAdded(trouteId, r.action.payload.photo.id)))
    },
    removePhoto: ({parentId, photoId}) => dispatch(trouteActions(objectType).photoRemoved(parentId, photoId)),
    loadTRoute: () => dispatch(
      objectActions.read(trouteId, {privacyCode})
    ),
    dirtyUpdateTRoute: record => dispatch(objectActions.dirtyUpdate(record)),
    // a trackpoints-only update to not clobber poi
    // NOTE: this won't work to update any other troute attributes!!
    updateTRoute: (record) => dispatch(objectActions.update(record.id, {[objectType]: {trackPoints: JSON.stringify(record.rawPoints)}})),
    setPreference: (preference, value) => userActions.setPreference(preference, value)
  }
}

const ConnectedTRouteShowPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableTRouteShowPage)

const parseObjectType = pathname => {
  if (pathname.match(/^\/routes\/\w+/i)) {
    return 'route'
  }
  if (pathname.match(/^\/trips\/\w+/i)) {
    return 'trip'
  }
  softError('Unable to determine object type for TRouteShowPage, defaulting to route')
  return 'route'
}

// and at the veeeeery top, we want to parse the trouteId into an int:
export const TRouteShowPage = ({params, ...props}) => (
  <ConnectedTRouteShowPage
    {...props}
    objectType={parseObjectType(props.location.pathname)}
    trouteId={parseInt(params.trouteId, 10)}
    privacyCode={props.location.query && props.location.query.privacy_code} />
)
